/** biome-ignore-all lint/complexity/noUselessFragments: false positive */
import { AuthToken } from '@cobliteam/api-lib/src/account/session';
import { Location } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { lazy } from '@cobliteam/lazy-with-retries';
import { SurveyHost } from '@cobliteam/surveys';
import AppAnnouncement from '@cobliteam/ui-business/components/AppAnnouncement/AppAnnouncement';
import DialogAlertHook from '@cobliteam/ui-business/components/dialogs/DialogAlertHook';
import DialogContractRenewal from '@cobliteam/ui-business/components/dialogs/DialogContractRenewal';
import InactiveFleetBanner from '@cobliteam/ui-business/components/InactiveFleetBanner/InactiveFleetBanner';
import { type ReactNode, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import BasicValueOnboarding from './components/BasicValueOnboarding/BasicValueOnboarding';
import CursorFlow from './components/CursorFlow/CursorFlow';
import PrivateRoute from './components/PrivateRoute';
import useRedirectAfterLogin from './hooks/useRedirectAfterLogin';
import AppLayout from './layouts/AppLayout';
import AppContentLoadingBoundary from './layouts/AppLayout/AppContentLoadingBoundary';
import AuthLayout from './layouts/AuthLayout';
import NoGroupsLayout from './layouts/NoGroupsLayout';
import HubSpotSSORedirect, { saveUrlToRedirectHubSpot } from './libs/HubSpotSSORedirect';
import { findRouteTeam } from './libs/routeOwnership';
import ErrorPage from './pages/ErrorPage';
import DashboardProvider, { useDashboard } from './providers/DashboardProvider';
import hasActiveFleetToSwitch from './providers/DashboardProvider/helpers/hasActiveFleetToSwitch';
import FeatureFlagsProvider from './providers/FeatureFlagsProvider';
import PrivateRouterProvider from './providers/PrivateRouterProvider';
import PublicRouterProvider from './providers/PublicRouterProvider';
import routes, { type IRoute } from './routes';

const Login = lazy(() => import('@cobliteam/login/src/pages/Login'));
const PasswordRecovery = lazy(() => import('@cobliteam/login/src/pages/PasswordRecovery'));
const NoFleetAccess = lazy(() => import('@cobliteam/login/src/pages/NoFleetAccess'));
const TokenValidation = lazy(() => import('@cobliteam/login/src/pages/TokenValidation'));
const PasswordReset = lazy(() => import('@cobliteam/login/src/pages/PasswordReset'));
const MfaChallenge = lazy(() => import('@cobliteam/login/src/pages/MfaChallenge'));

const LayoutSwitcher = ({ children }: { children: React.ReactNode }) => {
  const { runtimeFlags } = useDashboard();
  const location = useLocation();
  const { session, useGetUserData } = useUser();
  const { data: userData } = useGetUserData();

  // Frota inativa: só a página de boletos fica acessível (vem antes do gate de
  // grupos — uma frota inativa sem grupos válidos ainda precisa chegar aos boletos)
  if (runtimeFlags.isInactiveFleet) {
    if (location.pathname !== Location.BUSINESS_MANAGER_BOLETOS) {
      return <Redirect to={Location.BUSINESS_MANAGER_BOLETOS} />;
    }
    // Só outra frota ativa conta: a lista do getUserData pode devolver a própria frota da sessão
    // como ativa (dado velho / resolver parcial) e a dica não pode apontar para onde já se está.
    // Sem nenhuma (ou com a lista ainda carregando), a dica some.
    return (
      <AppLayout>
        <InactiveFleetBanner showSwitchFleetHint={!!session && hasActiveFleetToSwitch(session.fleet.id, userData?.userFleets)} />
        {children}
      </AppLayout>
    );
  }

  if (runtimeFlags.noValidGroupOrVehicles) {
    return (
      <AppContentLoadingBoundary>
        <NoGroupsLayout />
      </AppContentLoadingBoundary>
    );
  }
  return <AppLayout>{children}</AppLayout>;
};

// 🔐 Auth Routes
const Auth = () => (
  <Route exact path={[Location.LOGIN, Location.PASSWORD_RECOVERY, Location.TOKEN_VALIDATION, Location.PASSWORD_RESET, Location.MFA_CHALLENGE]}>
    <AuthLayout>
      <Route exact path={Location.LOGIN} component={Login} />
      <Route exact path={Location.PASSWORD_RECOVERY} component={PasswordRecovery} />
      <Route exact path={Location.TOKEN_VALIDATION} component={TokenValidation} />
      <Route exact path={Location.PASSWORD_RESET} component={PasswordReset} />
      <Route exact path={Location.MFA_CHALLENGE} component={MfaChallenge} />
    </AuthLayout>
  </Route>
);

// 🌐 Public Routes
const Public = () => (
  <PublicRouterProvider>
    <AppContentLoadingBoundary>
      <Route exact path={Location.NO_FLEET_ACCESS} component={NoFleetAccess} />
      <Auth />
    </AppContentLoadingBoundary>
  </PublicRouterProvider>
);

/**
 * Publishes the active route's owning team to Datadog RUM.
 *
 * Renders nothing — it exists so that an error caught by the generic `<Page>` ErrorBoundary,
 * which has no route awareness, still carries `team` and can be routed to `@team-<handle>`
 * by a monitor. Kept as an effect because this synchronises React state into an external
 * non-React system (the Datadog SDK), which is not derived state.
 */
const RumRouteOwnership = () => {
  const location = useLocation();
  const team = findRouteTeam(location.pathname, routes as IRoute[]);

  useEffect(() => {
    DatadogTrack.setGlobalContextProperty('team', team);
  }, [team]);

  return null;
};

// Portais ficam fora da AppContentLoadingBoundary: sem boundary própria, o namespace sob demanda de
// um dialog global suspenderia até a raiz e trocaria o app inteiro pelo loader.
const createGlobalPortal = (children: ReactNode) => createPortal(<Suspense fallback={null}>{children}</Suspense>, document.body);

// 🔒 Private Routes
const Private = () => {
  const { pathname } = useLocation();

  return (
    <PrivateRouterProvider>
      <HubSpotSSORedirect>
        <FeatureFlagsProvider>
          <DashboardProvider>
            <RumRouteOwnership />
            <LayoutSwitcher>
              {routes.map((route) => (
                <ErrorBoundary
                  key={route.path}
                  // An inline FallbackComponent remounts on every render; resetting on pathname instead avoids a reset/re-throw loop.
                  fallbackRender={({ error }) => <ErrorPage path={route.path} error={error} />}
                  resetKeys={[pathname]}
                  onError={(error, errorInfo) => {
                    DatadogTrack.error(error, {
                      component: 'Router',
                      route: route.path,
                      // Cast for the same reason the `<PrivateRoute route={route as IRoute} />`
                      // below does: `routes` is an un-annotated literal whose element union
                      // mixes this app's IRoute with the per-module IRoute interfaces.
                      team: (route as IRoute).team,
                      // Flattened alongside `errorInfo` because a minified production build reports
                      // React crashes as an opaque code (e.g. "Minified React error #130") with no
                      // component name — the stack is the only thing naming the culprit in Datadog.
                      componentStack: errorInfo?.componentStack,
                      errorInfo,
                    });
                  }}
                >
                  <PrivateRoute key={route.path} route={route as IRoute} />
                </ErrorBoundary>
              ))}
            </LayoutSwitcher>
            {createGlobalPortal(<AppAnnouncement />)}
            {createGlobalPortal(<DialogContractRenewal />)}
            {createGlobalPortal(<BasicValueOnboarding />)}
            {createGlobalPortal(<CursorFlow />)}
            {createGlobalPortal(<DialogAlertHook />)}
            {createGlobalPortal(<SurveyHost />)}
          </DashboardProvider>
        </FeatureFlagsProvider>
      </HubSpotSSORedirect>
    </PrivateRouterProvider>
  );
};

//  🔂 Redirect /# -> /
const PUBLIC_HASH_ROUTES: string[] = [Location.TOKEN_VALIDATION, Location.PASSWORD_RECOVERY, Location.PASSWORD_RESET];

const RedirectHash = () => {
  const authToken = localStorage.getItem(AuthToken.ACCESS_TOKEN);
  return (
    <Route
      path='/'
      render={({ location }) => {
        if (!location.hash.includes('#')) return null;
        const targetPath = location.hash.replace('#', '/').replace('//', '/');
        const isPublic = PUBLIC_HASH_ROUTES.includes(targetPath as Location);
        if (!(authToken || isPublic)) return <Redirect to={Location.LOGIN} />;
        return <Redirect to={targetPath} />;
      }}
    />
  );
};

const Router = () => {
  useRedirectAfterLogin();
  saveUrlToRedirectHubSpot();

  return (
    <Switch>
      <>
        {/* 🌐 Public Routes */}
        <Public />
        {/* 🔒 Private Routes */}
        <Private />
        {/* 🔂 Redirect /# -> / */}
        <RedirectHash />
      </>
    </Switch>
  );
};

export default Router;
